import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { getRouteGeo } from '../api';
import { Loader2, MapPin } from 'lucide-react';

const makeIcon = (color, label = '') =>
  L.divIcon({
    className: 'lf-marker',
    html: `
      <div style="
        width:28px;height:28px;transform:translate(-14px,-28px);
        position:relative;display:flex;align-items:center;justify-content:center;
      ">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="${color}" stroke="white" stroke-width="1.5">
          <path d="M12 2C8 2 5 5 5 9c0 5 7 13 7 13s7-8 7-13c0-4-3-7-7-7z"/>
          <circle cx="12" cy="9" r="2.5" fill="white"/>
        </svg>
        ${label ? `<span style="position:absolute;top:0;left:50%;transform:translateX(-50%);font-size:9px;font-weight:800;color:white;line-height:10px;padding-top:4px;pointer-events:none">${label}</span>` : ''}
      </div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 28],
  });

const originIcon = makeIcon('#2563eb');
const destIcon = makeIcon('#138a5b');
const stopIcon = (i) => makeIcon('#f59e0b', String(i));

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length >= 2) {
      const bounds = L.latLngBounds(points);
      map.fitBounds(bounds, { padding: [40, 40] });
    }
  }, [points, map]);
  return null;
}

export default function RouteMap({ origin, destination, stops = [], height = 320 }) {
  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const reqId = useRef(0);

  const stopsKey = stops.join('||');

  useEffect(() => {
    if (!origin || !destination) {
      setRoute(null);
      return;
    }
    const id = ++reqId.current;
    setLoading(true);
    setError('');
    getRouteGeo(origin, destination, stops)
      .then((r) => {
        if (id === reqId.current) setRoute(r);
      })
      .catch(() => {
        if (id === reqId.current) setError('Could not map this route.');
      })
      .finally(() => {
        if (id === reqId.current) setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origin, destination, stopsKey]);

  const points = useMemo(() => {
    if (!route) return [];
    return [
      [route.origin.lat, route.origin.lon],
      ...(route.stops || []).map((s) => [s.lat, s.lon]),
      [route.destination.lat, route.destination.lon],
    ];
  }, [route]);

  if (loading) {
    return (
      <div
        className="flex items-center justify-center gap-2 text-sm rounded-xl"
        style={{ height, background: '#f1f5fb', color: 'var(--lf-muted)' }}
      >
        <Loader2 className="animate-spin" size={16} /> Mapping route…
      </div>
    );
  }
  if (error) {
    return (
      <div
        className="flex items-center justify-center gap-2 text-sm rounded-xl"
        style={{ height, background: '#fef3f2', color: '#b42318' }}
      >
        <MapPin size={16} /> {error}
      </div>
    );
  }
  if (!route) {
    return (
      <div
        className="flex items-center justify-center gap-2 text-sm rounded-xl"
        style={{ height, background: '#f1f5fb', color: 'var(--lf-muted)' }}
      >
        <MapPin size={16} /> Set an origin and destination to see the route.
      </div>
    );
  }

  return (
    <div
      className="rounded-xl overflow-hidden"
      style={{ height, border: '1px solid var(--lf-line)' }}
    >
      <MapContainer
        center={points[0]}
        zoom={6}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={false}
      >
        <TileLayer
          attribution='&copy; OpenStreetMap contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <Marker position={points[0]} icon={originIcon}>
          <Popup>Origin: {route.origin.label}</Popup>
        </Marker>
        {(route.stops || []).map((s, i) => (
          <Marker
            key={`${s.label}-${i}`}
            position={[s.lat, s.lon]}
            icon={stopIcon(i + 1)}
          >
            <Popup>Stop {i + 1}: {s.label}</Popup>
          </Marker>
        ))}
        <Marker position={points[points.length - 1]} icon={destIcon}>
          <Popup>Destination: {route.destination.label}</Popup>
        </Marker>
        <Polyline
          positions={points}
          pathOptions={{ color: '#2563eb', weight: 4, opacity: 0.85, dashArray: '8 6' }}
        />
        <FitBounds points={points} />
      </MapContainer>
    </div>
  );
}
